import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const QRCodeDirectory = ({ allUsers, qrCodes, onUpdate }) => {
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const getUserQRCodes = (userId) => {
    return qrCodes?.filter(qr => qr?.user_id === userId);
  };

  const filteredUsers = allUsers?.filter(user => {
    if (filterRole !== 'all' && user?.role !== filterRole) return false;
    
    if (filterStatus !== 'all') {
      const userQRs = getUserQRCodes(user?.user_id);
      const hasActiveQR = userQRs?.some(qr => qr?.is_active);
      if (filterStatus === 'active' && !hasActiveQR) return false;
      if (filterStatus === 'inactive' && hasActiveQR) return false;
    }
    
    return true;
  });

  return (
    <div className="bg-card rounded-lg shadow-md p-6 border border-border">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Icon name="Users" size={24} className="text-primary" />
          <h2 className="text-xl font-bold text-foreground">User Directory</h2>
        </div>
        <Button
          onClick={onUpdate}
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
        >
          <Icon name="RefreshCw" size={16} />
          Refresh
        </Button>
      </div>
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Filter by Role
          </label>
          <Select
            value={filterRole}
            onChange={(e) => setFilterRole(e?.target?.value)}
          >
            <option value="all">All Roles</option>
            <option value="gym_owner">Gym Owners</option>
            <option value="staff">Staff</option>
            <option value="trainer">Trainers</option>
            <option value="member">Members</option>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Filter by QR Status
          </label>
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e?.target?.value)}
          >
            <option value="all">All Users</option>
            <option value="active">Has Active QR</option>
            <option value="inactive">No Active QR</option>
          </Select>
        </div>
      </div>
      {/* User List */}
      <div className="space-y-3">
        {filteredUsers?.map((user) => {
          const userQRs = getUserQRCodes(user?.user_id);
          const activeQRs = userQRs?.filter(qr => qr?.is_active);
          const totalScans = userQRs?.reduce((sum, qr) => sum + (qr?.scan_count || 0), 0);

          return (
            <div
              key={user?.user_id}
              className="p-4 bg-background rounded-lg border border-border hover:border-primary/50 transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="User" size={24} className="text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="font-bold text-foreground">{user?.user_profiles?.full_name}</h3>
                      <p className="text-sm text-muted-foreground">{user?.user_profiles?.email}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      user?.role === 'gym_owner' ? 'bg-purple-500/10 text-purple-500' :
                      user?.role === 'staff' ? 'bg-blue-500/10 text-blue-500' :
                      user?.role === 'trainer'? 'bg-green-500/10 text-green-500' : 'bg-gray-500/10 text-gray-500'
                    }`}>
                      {user?.role?.replace('_', ' ')?.toUpperCase()}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-3">
                    <div>
                      <p className="text-xs text-muted-foreground">Total QR Codes</p>
                      <p className="text-lg font-bold text-foreground">{userQRs?.length || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Active QR Codes</p>
                      <p className="text-lg font-bold text-green-500">{activeQRs?.length || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Total Scans</p>
                      <p className="text-lg font-bold text-blue-500">{totalScans}</p>
                    </div>
                  </div>

                  {activeQRs?.length > 0 && (
                    <div className="space-y-2">
                      {activeQRs?.slice(0, 2)?.map((qr) => (
                        <div
                          key={qr?.id}
                          className="p-2 bg-green-500/10 border border-green-500/20 rounded text-xs"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-foreground">
                              {qr?.source === 'staff' ? 'Staff QR' : 'Member QR'} - 
                              {qr?.expires_at ? ` Expires ${new Date(qr?.expires_at)?.toLocaleDateString()}` : ' Never expires'}
                            </span>
                            <span className="text-green-500 font-medium">ACTIVE</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {filteredUsers?.length === 0 && (
          <div className="text-center text-muted-foreground py-12">
            <Icon name="Users" size={48} className="mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">No Users Found</p>
            <p className="text-sm mt-2">Try adjusting your filters</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default QRCodeDirectory;